/**
 * @voltxampere/presets — Half-wave rectifier (Sprint 50-3)
 *
 * Single-diode half-wave rectifier converting AC into pulsed DC.
 * Companion to bridge-rectifier (Sprint 17). With V_AC peak 10V and
 * a 1N4148 diode, the load voltage tracks V_AC during the positive
 * half-cycle (minus diode drop ~0.7V) and is zero during the
 * negative half-cycle.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const halfWaveRectifier = {
    id: "half-wave-rectifier",
    name: { tr: "Yarım Dalga Doğrultucu", en: "Half-Wave Rectifier" },
    category: "power",
    difficulty: 2,
    description: {
        tr: "Tek diyot ile AC→DC dönüşüm. Pozitif yarım çevrim geçer, negatif kesilir.",
        en: "Single-diode AC→DC conversion. Positive half-cycle passes, negative is blocked.",
    },
    circuit: makeCircuit({
        slot: 1,
        name: "Half-Wave Rectifier",
        description: "Single diode + AC source + RC load",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "ac_in" }),
            makeNet({ name: "rect_out" }),
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 10 },
                terminals: { pos: "net_ac_in", neg: "net_gnd" },
                label: "V1",
                position: { x: -160, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "D1",
                type: "diode",
                params: { is: 1e-12, n: 1.0 },
                terminals: { anode: "net_ac_in", cathode: "net_rect_out" },
                label: "D1",
                position: { x: -40, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "R_LOAD",
                type: "resistor",
                params: { resistance: 1000 },
                terminals: { p1: "net_rect_out", p2: "net_gnd" },
                label: "R_LOAD",
                position: { x: 80, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -160, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [
            {
                id: "w_ac_in",
                netId: "net_ac_in",
                path: [
                    { x: -160, y: -10 },
                    { x: -160, y: -40 },
                    { x: -60, y: -40 },
                    { x: -60, y: 0 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_rect_out",
                netId: "net_rect_out",
                path: [
                    { x: -20, y: 0 },
                    { x: 80, y: 0 },
                    { x: 80, y: -20 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_gnd",
                netId: "net_gnd",
                path: [
                    { x: -160, y: 10 },
                    { x: -160, y: 71 },
                    { x: 80, y: 71 },
                    { x: 80, y: 20 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        {
            node: "rect_out",
            analysis: "dc",
            value: 9.3,
            tolerance: 0.1,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Diyot ileri yönde ~0.7V düşürür, ters yönde keser.",
            en: "Forward diode drop ~0.7V; reverse direction blocks current.",
        },
        {
            tr: "Yarım dalga = AC giriş yoğunluğunun yarısı.",
            en: "Half-wave only utilizes half the AC input cycle.",
        },
    ],
    references: ["Sedra/Smith, Microelectronic Circuits, Ch. 4"],
};
//# sourceMappingURL=half-wave-rectifier.js.map